// src/pages/modules/EscalationHistoryLogPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MoreHorizontal, Eye, Loader2, ArrowUpRight, FileText, MoreVertical } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import LogDataDisplay from '../../components/common/LogDataDisplay';
import {
  fetchActivityLogs,
  clearError,
} from '../../store/slices/activityLogSlice';
import { formatDateTime } from 'utlis/formateDateTime';

const EscalationHistoryLogPage = () => {
  usePermissionSocket();

  const dispatch = useDispatch();
  const { logs, pagination, isLoading, error } = useSelector((state) => state.activityLog);

  const { canRead, canExport } = usePermissions('escalation_history');

  // Pagination & Search
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Reset page on search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); // Always go back to page 1 when user types
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refetch with resource=escalation_history filter using activity log API
  const refetch = useCallback(() => {
    if (canRead) {
      dispatch(fetchActivityLogs({
        page,
        page_size: pageSize,
        search: searchTerm,
        resource: 'escalation_history',
        // resource: 'auto_escalate'  
      }));
    }
  }, [dispatch, page, pageSize, searchTerm, canRead]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load escalation history');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const getUserName = (log) => {
    if (!log.created_by) return 'System';
    const { first_name = '', last_name = '', code = '' } = log.created_by || {};
    return `${first_name} ${last_name}`.trim() || code || 'Unknown User';
  };

  const getUserCode = (log) => {
    return log.created_by?.code || 'N/A';
  };

  // Badge for content type
  const getContentTypeBadge = (type) => {
    const map = {
      ticket: { label: 'Ticket', variant: 'default' },
      request: { label: 'Request', variant: 'secondary' },
      leave: { label: 'Leave', variant: 'outline' },
      grievance: { label: 'Grievance', variant: 'destructive' },
    };
    const config = map[type] || { label: type, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Human readable summary
  const generateSummary = (log) => {
    const user = getUserName(log);
    const type = log.content_type;
    const repr = log.object_repr || 'Unknown Item';

    if (type === 'ticket') {
      return `${user} escalated ticket "${repr}" (Level ${log.order})`;
    }
    if (type === 'request') {
      return `${user} escalated request "${repr}" to Level ${log.order}`;
    }
    if (type === 'leave') {
      return `${user} escalated leave request to Level ${log.order}`;
    }
    if (type === 'grievance') {
      return `${user} escalated grievance to Level ${log.order}`;
    }

    return `${user} escalated ${type} to Level ${log.order}`;
  };

  // Transform log data to match the expected format for LogDataDisplay
  const transformLogData = (log) => {
    if (!log.data) return null;

    // If the data already has the expected format, return as is
    if (log.data.old || log.data.new || log.data.changes) {
      return log.data;
    }

    // Otherwise, transform the flat data structure to the expected format
    // For create actions, put everything in 'new'
    if (log.action_type === 'create') {
      return {
        new: log.data,
        old: {},
        changes: {}
      };
    }

    // For delete actions, put everything in 'old'
    if (log.action_type === 'delete') {
      return {
        old: log.data,
        new: {},
        changes: {}
      };
    }

    // For update actions, try to determine what changed
    return {
      old: log.data.old || {},
      new: log.data.new || {},
      changes: log.data.changes || {}
    };
  };

  const getResourceBadge = (resource) => {
    const map = { escalation_history: 'secondary' };
    return <Badge variant={map[resource] || 'default'}>{resource || 'unknown'}</Badge>;
  };

  const getActionBadge = (action) => {
    const map = {
      create: 'success',
      update: 'default',
      delete: 'destructive'
    };
    return <Badge variant={map[action] || 'secondary'}>{(action || '').toUpperCase()}</Badge>;
  };

  const handleViewDetails = (log) => {
    if (!canRead) {
      toast.error("You don't have permission to view details");
      return;
    }
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const columns = [
    {
      accessorKey: 'resource_display',
      header: 'Resource',
      cell: ({ row }) => getResourceBadge(row.original.resource_display || row.original.resource),
    },
    {
      accessorKey: 'action_type',
      header: 'Type',
      cell: ({ row }) => getContentTypeBadge(row.original.action_type),
    },
    // {
    //   accessorKey: 'object_repr',
    //   header: 'Item',
    //   cell: ({ row }) => (
    //     <div className="flex items-center gap-2 font-medium">
    //       <FileText className="h-4 w-4 text-muted-foreground" />
    //       <span className="truncate max-w-xs">{row.original.object_repr || '—'}</span>
    //     </div>
    //   ),
    // },
    {
      accessorKey: 'order',
      header: 'Escalation Level',
      cell: ({ row }) => (
        <Badge variant="outline" className="font-mono">
          Level {row.original?.data?.new?.order}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_by',
      header: 'Performed By',
      cell: ({ row }) => <span className="font-medium">{getUserName(row.original)}</span>,
    },
    {
      accessorKey: 'created_at',
      header: 'Escalated At',
      cell: ({ row }) => {
        return (
          <div className="text-sm">
            <div>{formatDateTime(row.original.created_at)}</div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleViewDetails(row.original)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Escalation History Log"
        description="Track all escalations across tickets, requests, leaves & grievances"
      />

      {/* {isLoading && logs.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin mr-3" />
          <span className="text-lg">Loading escalation history...</span>
        </div>
      )} */}

      {!canRead && !isLoading && (
        <div className="text-center py-16 text-muted-foreground text-lg">
          You don't have permission to view escalation history.
        </div>
      )}

      {canRead && (
        <DataTable
          data={logs}
          totalCount={pagination.count}
          columns={columns}
          searchPlaceholder="Search by ticket, request, grievance, level..."
          showExport={false}
          showImport={false}
          onExport={() => toast.info("Export coming soon")}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={pagination.total_pages || 1}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
        />
      )}

      {/* Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl">Escalation Details</DialogTitle>
            <DialogDescription>
              {selectedLog && formatDateTime(selectedLog.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-8 py-4">
                {/* Summary Card */}
                <div className="bg-card border rounded-lg p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      {/* <p className="text-sm text-muted-foreground">Escalated Item</p> */}
                      {/* <p className="text-lg font-semibold flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        {selectedLog.object_repr || 'Unknown'}
                      </p> */}
                      <p className="text-sm text-muted-foreground">Performed by: {getUserName(selectedLog)}</p>
                      {/* <p className="text-sm text-muted-foreground">Code: {getUserCode(selectedLog)}</p> */}
                    </div>
                    <div className="text-right">
                      {getResourceBadge(selectedLog.resource_display || selectedLog.resource)}
                      <div className="mt-2">
                        {getContentTypeBadge(selectedLog.action_type)}
                      </div>
                      <div className="mt-2">
                        <Badge variant="outline" className="text-lg font-mono">
                          Level {selectedLog.data?.new?.order}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground">Summary</p>
                    <p className="mt-1 text-foreground">{generateSummary(selectedLog)}</p>
                  </div> */}
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-sm bg-muted/30 rounded-lg p-4">
                  <div>
                    <span className="text-muted-foreground">Object Type</span>
                    <p className="font-medium capitalize">{selectedLog.content_type}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Object ID</span>
                    <p className="font-mono text-xs">{selectedLog.object_id}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Escalation ID</span>
                    <p className="font-mono text-xs">{selectedLog.id}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Updated At</span>
                    <p>{formatDateTime(selectedLog.updated_at)}</p>
                  </div>
                </div>

                {/* Log Data Display - Reusable Component */}
                <LogDataDisplay logData={transformLogData(selectedLog)} />
              </div>
            </ScrollArea>
          )}

          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EscalationHistoryLogPage;